import { useState } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Tabs,
  Loader,
  Alert,
  List,
  SimpleGrid,
  Card,
  ThemeIcon,
  Textarea,
  Button,
  NumberInput,
  TextInput,
} from '@mantine/core';
import { notifications as _notifications } from '@mantine/notifications';
import {
  IconBrain,
  IconHeartbeat,
  IconTruck,
  IconCash,
  IconCertificate,
  IconAlertTriangle,
  IconSearch,
  IconChefHat,
} from '@tabler/icons-react';
import {
  useAIAccountHealth,
  useAIAccountInsights,
  useAIPaymentRisk,
  useAIPaymentOptimize,
  useAIDocCompliance,
  useAIClaimAlert,
  useAIDeliveryPatterns,
  useAIPriceSensitivity as _useAIPriceSensitivity,
  useAIProductSearch,
  useAIMenuPlanner,
  useAIRecipeParser,
} from '@/hooks/usePortalApi';

const RISK_COLORS: Record<string, string> = {
  low: 'green',
  medium: 'yellow',
  high: 'orange',
  critical: 'red',
  none: 'gray',
  info: 'blue',
  warning: 'yellow',
};

function HealthCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <Card shadow="xs" p="md">
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="xl" fw={700} c={color}>{value}</Text>
    </Card>
  );
}

function AccountHealthTab() {
  const { data, isLoading } = useAIAccountHealth();
  const { data: insights } = useAIAccountInsights();

  if (isLoading) return <Loader />;

  return (
    <Stack>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <HealthCard label="Health Score" value={data?.health_score ?? 'N/A'} color="teal" />
        <HealthCard label="Risk Level" value={data?.risk_level ?? 'N/A'} color={RISK_COLORS[data?.risk_level] ?? 'gray'} />
        <HealthCard label="Payment Score" value={data?.payment_score ?? 'N/A'} color="blue" />
        <HealthCard label="Order Trend" value={data?.order_trend ?? 'N/A'} color="violet" />
      </SimpleGrid>
      {insights?.recommendations && (
        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">AI Recommendations</Text>
          <List>
            {(insights.recommendations as string[]).map((r: string, i: number) => (
              <List.Item key={i}>{r}</List.Item>
            ))}
          </List>
        </Paper>
      )}
    </Stack>
  );
}

function PaymentInsightsTab() {
  const { data: risk, isLoading: riskLoading } = useAIPaymentRisk();
  const { data: optimize } = useAIPaymentOptimize();

  if (riskLoading) return <Loader />;

  return (
    <Stack>
      {risk?.risk_level && risk.risk_level !== 'low' && (
        <Alert color={RISK_COLORS[risk.risk_level]} title="Payment Risk Alert">
          {risk.message ?? `Your account has ${risk.risk_level} payment risk.`}
        </Alert>
      )}
      {optimize?.suggestions && (
        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">Payment Optimization Tips</Text>
          <List>
            {(optimize.suggestions as any[]).map((s: any, i: number) => (
              <List.Item key={i}>
                <Text fw={500}>{s.title ?? s.type}</Text>
                <Text size="sm" c="dimmed">{s.description ?? s.detail}</Text>
              </List.Item>
            ))}
          </List>
        </Paper>
      )}
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <HealthCard label="Overdue Amount" value={risk?.overdue_amount ? `$${risk.overdue_amount.toLocaleString()}` : '$0'} color="red" />
        <HealthCard label="Early Pay Savings" value={optimize?.potential_savings ? `$${optimize.potential_savings.toLocaleString()}` : '$0'} color="green" />
      </SimpleGrid>
    </Stack>
  );
}

function DeliveryInsightsTab() {
  const { data, isLoading } = useAIDeliveryPatterns();

  if (isLoading) return <Loader />;

  return (
    <Stack>
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <HealthCard label="Avg Success Rate" value={data?.avg_delivery_success_rate ? `${(data.avg_delivery_success_rate * 100).toFixed(0)}%` : 'N/A'} color="green" />
        <HealthCard label="Preferred Window" value={data?.preferred_window ?? 'N/A'} color="blue" />
        <HealthCard label="Common Issues" value={data?.common_issues?.length ?? 0} color="orange" />
      </SimpleGrid>
      {data?.recommendations && (
        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">Delivery Recommendations</Text>
          <List>
            {(data.recommendations as string[]).map((r: string, i: number) => (
              <List.Item key={i}>{r}</List.Item>
            ))}
          </List>
        </Paper>
      )}
    </Stack>
  );
}

function ComplianceTab() {
  const { data: compliance, isLoading } = useAIDocCompliance();
  const { data: claimAlert } = useAIClaimAlert();

  if (isLoading) return <Loader />;

  return (
    <Stack>
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <HealthCard
          label="Compliance Rate"
          value={compliance?.compliance_rate ? `${(compliance.compliance_rate * 100).toFixed(0)}%` : 'N/A'}
          color={compliance?.compliance_rate >= 0.9 ? 'green' : 'orange'}
        />
        <HealthCard label="Expiring Soon" value={compliance?.expiring_soon ?? 0} color="yellow" />
        <HealthCard label="Missing Docs" value={compliance?.missing_required ?? 0} color="red" />
      </SimpleGrid>
      {claimAlert?.alert_level && claimAlert.alert_level !== 'none' && (
        <Alert
          color={RISK_COLORS[claimAlert.alert_level]}
          title="Claim Pattern Alert"
          icon={<IconAlertTriangle />}
        >
          <Text>{claimAlert.overall_assessment}</Text>
          {claimAlert.repeat_items?.map((item: any, i: number) => (
            <Text key={i} size="sm" mt="xs">
              {item.commodity}: {item.claim_count} claims - {item.ai_diagnosis}
            </Text>
          ))}
        </Alert>
      )}
    </Stack>
  );
}

function ProductDiscoveryTab() {
  const searchMut = useAIProductSearch();
  const menuMut = useAIMenuPlanner();
  const recipeMut = useAIRecipeParser();
  const [searchQuery, setSearchQuery] = useState('');
  const [eventType, setEventType] = useState('');
  const [guestCount, setGuestCount] = useState<number | ''>(50);
  const [recipeText, setRecipeText] = useState('');

  return (
    <Stack>
      <Paper p="md" withBorder>
        <Text fw={600} mb="sm">Natural Language Product Search</Text>
        <Group>
          <TextInput
            style={{ flex: 1 }}
            placeholder="e.g. organic berries for a smoothie bar..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
          />
          <Button
            loading={searchMut.isPending}
            onClick={() => searchMut.mutate({ query: searchQuery })}
            disabled={searchQuery.length < 2}
          >Search</Button>
        </Group>
        {searchMut.data?.products && (
          <List mt="md">
            {searchMut.data.products.map((p: any, i: number) => (
              <List.Item key={i}>
                <Group>
                  <Text fw={500}>{p.name}</Text>
                  <Text c="dimmed">{p.sku}</Text>
                  <Badge>{p.match_score ? `${(p.match_score * 100).toFixed(0)}% match` : ''}</Badge>
                </Group>
              </List.Item>
            ))}
          </List>
        )}
      </Paper>

      <Paper p="md" withBorder>
        <Text fw={600} mb="sm">AI Menu Planner</Text>
        <Group>
          <TextInput
            placeholder="Event type (wedding, corporate, bbq...)"
            value={eventType}
            onChange={e => setEventType(e.currentTarget.value)}
            leftSection={<IconChefHat size={16} />}
          />
          <NumberInput
            placeholder="Guests"
            value={guestCount}
            onChange={(v) => setGuestCount(typeof v === 'number' ? v : '')}
            min={1}
            max={10000}
            w={100}
          />
          <Button
            loading={menuMut.isPending}
            onClick={() => menuMut.mutate({ event_type: eventType, guest_count: Number(guestCount) })}
            disabled={!eventType || !guestCount}
          >Plan Menu</Button>
        </Group>
        {menuMut.data?.menu_items && (
          <List mt="md">
            {menuMut.data.menu_items.map((m: any, i: number) => (
              <List.Item key={i}>{m.name} — qty: {m.quantity} ({m.category})</List.Item>
            ))}
          </List>
        )}
      </Paper>

      <Paper p="md" withBorder>
        <Text fw={600} mb="sm">Recipe-to-Cart</Text>
        <Textarea
          placeholder="Paste a recipe here and we'll find matching products..."
          value={recipeText}
          onChange={e => setRecipeText(e.currentTarget.value)}
          rows={4}
        />
        <Button
          mt="sm"
          loading={recipeMut.isPending}
          onClick={() => recipeMut.mutate({ recipe_text: recipeText })}
          disabled={recipeText.length < 10}
        >Parse Recipe</Button>
        {recipeMut.data?.ingredients && (
          <List mt="md">
            {recipeMut.data.ingredients.map((ing: any, i: number) => (
              <List.Item key={i}>
                {ing.ingredient}: {ing.matched_product ?? 'No match'} — {ing.quantity} {ing.unit}
              </List.Item>
            ))}
          </List>
        )}
      </Paper>
    </Stack>
  );
}

export default function AIInsights() {
  return (
    <Container size="lg" py="md">
      <Group mb="lg">
        <ThemeIcon size="xl" variant="light" color="violet">
          <IconBrain size={28} />
        </ThemeIcon>
        <div>
          <Title order={2}>AI Insights</Title>
          <Text c="dimmed">Personalized intelligence for your account</Text>
        </div>
      </Group>

      <Tabs defaultValue="health">
        <Tabs.List mb="md">
          <Tabs.Tab value="health" leftSection={<IconHeartbeat size={16} />}>Account Health</Tabs.Tab>
          <Tabs.Tab value="payments" leftSection={<IconCash size={16} />}>Payment Insights</Tabs.Tab>
          <Tabs.Tab value="delivery" leftSection={<IconTruck size={16} />}>Delivery</Tabs.Tab>
          <Tabs.Tab value="compliance" leftSection={<IconCertificate size={16} />}>Compliance</Tabs.Tab>
          <Tabs.Tab value="discover" leftSection={<IconSearch size={16} />}>Product Discovery</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="health"><AccountHealthTab /></Tabs.Panel>
        <Tabs.Panel value="payments"><PaymentInsightsTab /></Tabs.Panel>
        <Tabs.Panel value="delivery"><DeliveryInsightsTab /></Tabs.Panel>
        <Tabs.Panel value="compliance"><ComplianceTab /></Tabs.Panel>
        <Tabs.Panel value="discover"><ProductDiscoveryTab /></Tabs.Panel>
      </Tabs>
    </Container>
  );
}
